import React, { useState, useEffect, useCallback } from 'react';
import {
  useDataProvider,
  useNotify,
  useAuthenticated,
  usePermissions,
  useTranslate,
} from 'react-admin';
import './Settings.css';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business,
  PhotoCamera,
  Language,
  Save,
  Cancel,
  Edit,
  Payment as PaymentIcon,
  Palette,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { clearPublicSettingsCache } from '../../utils/publicApi';
import { tokenManager } from '../../utils/tokenManager';
import { TimezoneSelector } from './TimezoneSelector';
import { translateServerMessage } from '../../utils/translateServerMessage';

const SETTING_KNOWN_FIELDS = new Set([
  'value', 'translations', 'key', 'type', 'category', 'description',
]);

const mapSettingBackendPath = (path: string): string =>
  path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

interface Setting {
  id: string;
  key: string;
  value: any;
  displayValue: any;
  type: 'string' | 'number' | 'boolean' | 'json' | 'file' | 'secret';
  description?: string;
  updatedAt: string;
  translations?: Record<string, string>;
}

interface GroupedSettings {
  branding?: Setting[];
  appearance?: Setting[];
  system?: Setting[];
  payment?: Setting[];
  other?: Setting[];
}

const SettingsList = () => {
  useAuthenticated();
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<GroupedSettings>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [translationDialog, setTranslationDialog] = useState<{
    open: boolean;
    setting: Setting | null;
  }>({ open: false, setting: null });
  const [hasFetched, setHasFetched] = useState(false);
  const [tabsReady, setTabsReady] = useState(false);

  const canRead = permissions?.includes('settings:read:all') || 
                  permissions?.includes('system:admin:all') ||
                  permissions?.includes('users:admin:all');
  const canEdit = permissions?.includes('settings:write:all') || 
                  permissions?.includes('system:admin:all') ||
                  permissions?.includes('users:admin:all');

  const fetchSettings = useCallback(async () => {
    if (!canRead) return;
    
    console.log('Fetching settings...');
    setLoading(true);
    try {
      const response = await apiClient.get<any>(`${API_CONFIG.endpoints.backend.settings}`);
      console.log('Settings response:', response);
      setSettings(response.settings || {});
    } catch (error: any) {
      console.error('Error loading settings:', error);
      const errorMessage = error.errorCode 
        ? translate(`errors.${error.errorCode}`)
        : translate('settings.errors.loadFailed', { _: 'Failed to load settings' });
      notify(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [canRead, notify, translate]);

  // Fetch settings on component mount
  useEffect(() => {
    if (canRead && !hasFetched && !loading) {
      void fetchSettings();
    }
  }, [canRead, hasFetched, loading, fetchSettings]);

  // Delay tabs rendering to avoid ResizeObserver issues
  useEffect(() => {
    const timer = setTimeout(() => {
      setTabsReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Returns a map of field-level errors (keyed by field name). Empty map = success.
  const handleSave = async (key: string, value: any, translations?: Record<string, string>): Promise<Record<string, string>> => {
    try {
      await dataProvider.update('settings', {
        id: key,
        data: { value, translations },
        previousData: {} as any,
      });
      notify(translate('ra.notification.updated', { smart_count: 1 }), { type: 'success' });
      setEditingKey(null);
      setHasFetched(false); // Force refetch
      // Clear public settings cache if branding settings were updated
      if (key === 'company_name' || key === 'company_logo') {
        clearPublicSettingsCache();
      }
      return {};
    } catch (error: any) {
      const apiError = error?.body || error;
      const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
      const fieldErrors: Record<string, string> = {};
      const summaryLines: string[] = [];
      if (rawErrors) {
        for (const [path, msgs] of Object.entries(rawErrors)) {
          const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
          const translated = translateServerMessage(rawMsg, translate);
          const field = mapSettingBackendPath(path);
          fieldErrors[field] = translated;
          const labelKey = `settings.fields.${key}`;
          const label = SETTING_KNOWN_FIELDS.has(field)
            ? translate(labelKey, { _: key.replace(/_/g, ' ') })
            : field;
          summaryLines.push(`\u2022 ${label}: ${translated}`);
        }
      }

      const headerKey = apiError?.errorCode || apiError?.error_code
        ? `errors.${apiError.errorCode || apiError.error_code}`
        : 'settings.errors.updateFailed';
      const header = translate(headerKey, {
        _: apiError?.message || translate('settings.errors.updateFailed', { _: 'Failed to update setting' })
      });

      if (summaryLines.length > 0) {
        notify(`${header}\n${summaryLines.join('\n')}`, {
          type: 'error',
          multiLine: true,
          autoHideDuration: 10000,
        });
      } else {
        notify(header, { type: 'error', autoHideDuration: 6000 });
      }

      return fieldErrors;
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      notify(translate('settings.invalidFileType'), { type: 'error' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify(translate('settings.fileTooLarge'), { type: 'error' });
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('logo', file);

    try {
      // Get valid access token using tokenManager
      const accessToken = await tokenManager.getAccessToken();
      if (!accessToken) {
        notify(translate('settings.authRequired'), { type: 'error' });
        return;
      }

      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.backend.settings}/upload-logo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.errorCode 
          ? translate(`errors.${errorData.errorCode}`)
          : translate('settings.errors.uploadFailed', { _: 'Failed to upload logo' });
        notify(errorMessage, { type: 'error' });
        return;
      }

      const result = await response.json();
      notify(translate('settings.logoUploaded', { _: 'Logo uploaded successfully' }), { type: 'success' });
      
      // Immediately update the logo in the current settings state
      setSettings(prevSettings => {
        const updatedSettings = { ...prevSettings };
        if (updatedSettings.branding) {
          updatedSettings.branding = updatedSettings.branding.map(setting => 
            setting.key === 'company_logo' 
              ? { ...setting, value: result.logoUrl, displayValue: result.logoUrl }
              : setting
          );
        }
        return updatedSettings;
      });
      
      setHasFetched(false); // Force refetch
      // Clear public settings cache
      clearPublicSettingsCache();
    } catch (error) {
      notify('Failed to upload logo', { type: 'error' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const SettingItem = ({ setting }: { setting: Setting }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(setting.value);
    const [fieldError, setFieldError] = useState<string>('');

    const handleEdit = () => {
      setIsEditing(true);
      setEditingKey(setting.key);
      setFieldError('');
    };

    const handleCancel = () => {
      setIsEditing(false);
      setEditingKey(null);
      setValue(setting.value);
      setFieldError('');
    };

    const handleSaveClick = async () => {
      const errors = await handleSave(setting.key, value);
      if (Object.keys(errors).length > 0) {
        // Prefer the 'value' field error if present; otherwise the first error.
        setFieldError(errors.value || Object.values(errors)[0] || '');
      } else {
        setFieldError('');
        setIsEditing(false);
      }
    };

    const renderValue = () => {
      if (setting.type === 'file' && setting.key === 'company_logo') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={setting.value}
              sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 } }}
              variant="rounded"
            >
              {!setting.value && <Business sx={{ fontSize: 40 }} />}
            </Avatar>
            {canEdit && (
              <label htmlFor="logo-upload">
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                />
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={uploadingLogo ? <CircularProgress size={20} /> : <PhotoCamera />}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? translate('settings.uploading') : translate('settings.changeLogo')}
                </Button>
              </label>
            )}
          </Box>
        );
      }

      // Special handling for timezone setting
      if (setting.key === 'system_timezone') {
        return (
          <TimezoneSelector
            currentValue={setting.value}
            canEdit={canEdit}
            onSave={async (value) => {
              await handleSave(setting.key, value);
            }}
          />
        );
      }

      if (isEditing && editingKey === setting.key) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                if (fieldError) setFieldError('');
              }}
              variant="outlined"
              size="small"
              type={setting.type === 'secret' ? 'password' : 'text'}
              autoFocus
              error={!!fieldError}
              helperText={fieldError || undefined}
            />
            <IconButton onClick={handleSaveClick} color="primary">
              <Save />
            </IconButton>
            <IconButton onClick={handleCancel}>
              <Cancel />
            </IconButton>
          </Box>
        );
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body1">
            {setting.type === 'secret' && setting.value
              ? '••••••••'
              : (setting.displayValue || setting.value)}
          </Typography>
          {canEdit && setting.type !== 'file' && (
            <>
              <IconButton onClick={handleEdit} size="small" color="primary">
                <Edit fontSize="small" />
              </IconButton>
              {setting.key === 'company_name' && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Language />}
                  onClick={() => setTranslationDialog({ open: true, setting })}
                >
                  {translate('settings.translations')}
                </Button>
              )}
            </>
          )}
        </Box>
      );
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {translate(`settings.fields.${setting.key}`, {
            _: setting.key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
          })}
        </Typography>
        {renderValue()}
        {setting.description && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {translate(`settings.descriptions.${setting.key}`, { _: setting.description })}
          </Typography>
        )}
      </Box>
    );
  };

  const TranslationDialog = () => {
    const { setting } = translationDialog;
    const { control, handleSubmit, reset, setError } = useForm({
      defaultValues: {
        translations: setting?.translations || {},
      },
    });

    const onSubmit = async (data: any) => {
      if (setting) {
        const errors = await handleSave(setting.key, setting.value, data.translations);
        if (Object.keys(errors).length > 0) {
          // Keep dialog open; propagate field-level errors into form fields if they match.
          for (const [field, message] of Object.entries(errors)) {
            if (field.startsWith('translations.') || field === 'translations') {
              setError(field as any, { type: 'server', message });
            }
          }
          return;
        }
        setTranslationDialog({ open: false, setting: null });
        reset();
      }
    };

    return (
      <Dialog
        open={translationDialog.open}
        onClose={() => setTranslationDialog({ open: false, setting: null })}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ wordBreak: 'break-word' }}>{translate('settings.manageTranslations')}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {translate('settings.originalValue')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {setting?.value}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                {translate('settings.traditionalChinese')}
              </Typography>
              <Controller
                name="translations.zh-TW"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    placeholder={translate('settings.enterTranslation')}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || undefined}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {/* Add more language fields as needed */}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTranslationDialog({ open: false, setting: null })}>
              {translate('settings.cancel')}
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {translate('settings.saveTranslations')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  };

  const categories = [
    { label: translate('settings.categories.branding'), value: 'branding', icon: <Business /> },
    { label: translate('settings.categories.appearance'), value: 'appearance', icon: <Palette /> },
    { label: translate('settings.categories.system'), value: 'system', icon: <SettingsIcon /> },
    { label: translate('settings.categories.payment'), value: 'payment', icon: <PaymentIcon /> },
  ];

  const currentCategory = categories[activeTab]?.value as keyof GroupedSettings;
  const currentSettings = settings[currentCategory] || [];

  // Check permissions after hooks
  if (!canRead) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
        <Alert severity="error">
          {translate('settings.noPermissionView')}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
        {translate('menu.settings')}
      </Typography>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('settings.noPermissionEdit')}
        </Alert>
      )}

      <Card sx={{ overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {tabsReady ? (
            <>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant={isMobile ? "fullWidth" : "scrollable"}
                scrollButtons={isMobile ? false : "auto"}
                allowScrollButtonsMobile
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 3,
                  minHeight: { xs: 40, sm: 48 },
                  '& .MuiTab-root': {
                    minWidth: { xs: 0, sm: 90 },
                    minHeight: { xs: 40, sm: 48 },
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 0.75, sm: 1.5 },
                    fontSize: { xs: '0.65rem', sm: '0.875rem' },
                    lineHeight: 1.1,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    textTransform: 'none',
                  },
                }}
              >
                {categories.map((category) => (
                  <Tab
                    key={category.value}
                    label={category.label}
                    icon={isMobile ? undefined : category.icon}
                    iconPosition="start"
                  />
                ))}
              </Tabs>

              {currentSettings.length === 0 ? (
                <Typography color="text.secondary">
                  {translate('settings.noSettings')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {currentSettings.map((setting) => (
                    <SettingItem key={setting.key} setting={setting} />
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </CardContent>
      </Card>

      <TranslationDialog />
    </Container>
  );
};

export default SettingsList;